/**
 * GalleryDesignThumb — a live picture of a design.
 *
 * GAL-14. The design cards and previews used to show a static render
 * of each handoff prototype — so every card carried the prototype's
 * demo couple, whatever the studio had typed. This renders the real
 * template through the real engine instead, inside a scaled,
 * non-interactive frame. Two callers, two subjects:
 *
 *   • The PREVIEW of a chosen design (New gallery step 2, the editor's
 *     Design section) shows THE STUDIO'S gallery — its typed name, its
 *     studio, its own photos once it has some.
 *   • The CARDS in the picker and the hub rail were each their own
 *     little gallery: one demo couple per design (DESIGN_DEMO_NAMES),
 *     the sample album. GALLERY-2 moved every card and rail tile to
 *     the design's static cover (`designCoverSrc`); this component now
 *     serves the live looks — the customize preview, the editor's
 *     current design and the marketing page's featured slot.
 *
 * The template only mounts once it scrolls into view, so a 23-card
 * picker loads a screen's worth at a time. The payload is demo-scoped
 * (`demo-thumb`), so the engine keeps everything in memory and never
 * touches Supabase.
 */
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import type { PublicGalleryPayload } from '@/services/galleries';
import { DEFAULT_TEMPLATE_ID, GALLERY_THEMES, googleFontsHref } from '../templates/themes';
import type { GalleryCustomization, GalleryTemplateId } from '../templates/types';
import { resolveTheme, useGalleryEngine } from '../templates/useGalleryEngine';
import { useGoogleFonts } from '../templates/useGoogleFonts';
import { getLazyTemplate } from '../templates/GalleryTemplateRenderer';

/**
 * One demo couple per design, for the cards. Editorial (01) keeps the
 * handoff's reference couple; every other design gets its own, so no
 * two cards read the same. All use the `A & B` shape every design's
 * title treatment is built around (TitleAmp splits on it).
 */
export const DESIGN_DEMO_NAMES: Record<GalleryTemplateId, string> = {
  basic: 'Maya & Theo',
};

export const demoNameFor = (id: GalleryTemplateId): string => DESIGN_DEMO_NAMES[id] ?? 'Sofia & Marco';

export interface ThumbPhoto {
  id: string;
  url: string;
  width?: number | null;
  height?: number | null;
  name?: string;
}

export interface GalleryDesignThumbProps {
  template: GalleryTemplateId;
  /** The gallery's name — what the design sets as its title. */
  name: string;
  studioName?: string | null;
  logoUrl?: string | null;
  brandColor?: string | null;
  customization?: GalleryCustomization;
  /** The gallery's own photos; the sample album when empty. */
  photos?: ThumbPhoto[];
  /** Virtual viewport width the design is laid out at before scaling. */
  frameWidth?: number;
  /** Show the design's download chrome. Off on the marketing page. */
  downloadEnabled?: boolean;
  className?: string;
}

/** The handoff's sample album (public/gallery-designs/album). */
const SAMPLE_ALBUM: ThumbPhoto[] = [
  { id: 'sample-1', url: '/gallery-designs/album/01.jpg', width: 933, height: 1400, name: 'Red room' },
  { id: 'sample-2', url: '/gallery-designs/album/02.jpg', width: 1120, height: 1400, name: 'Close' },
  { id: 'sample-3', url: '/gallery-designs/album/03.jpg', width: 934, height: 1400, name: 'Doorway' },
  { id: 'sample-4', url: '/gallery-designs/album/04.jpg', width: 1400, height: 933, name: 'Silver' },
  { id: 'sample-5', url: '/gallery-designs/album/05.jpg', width: 1400, height: 674, name: 'Coast' },
];

/** Five films; each takes its poster from the album (index into it). */
const SAMPLE_FILMS: Array<[string, number, number]> = [
  ['Highlight film.mp4', 134, 3],
  ['Teaser.mp4', 45, 1],
  ['Full ceremony.mp4', 1102, 2],
  ['Social cut.mp4', 30, 0],
  ['Coast.mp4', 88, 4],
];

const isFilmDesign = (id: GalleryTemplateId) => GALLERY_THEMES[id]?.kind === 'video';

export interface DemoPayloadOptions {
  template: GalleryTemplateId;
  name: string;
  studioName?: string | null;
  logoUrl?: string | null;
  brandColor?: string | null;
  customization?: GalleryCustomization;
  photos?: ThumbPhoto[];
  /** Demo-scoped gallery id (must keep the `demo-` prefix). */
  galleryId?: string;
  /**
   * A real stream for the sample films, so a live demo can actually
   * play. Thumbs leave it unset — they only ever show the posters.
   */
  filmSource?: { playbackId: string; mp4Url: string };
  /**
   * Whether the design shows its download chrome (MKTG-GAL14d). True
   * everywhere inside the app — a real gallery hands over files, and a
   * studio picking a design should see that. FALSE on the marketing
   * page: those frames are Rennan's own clients' wedding, shown to
   * strangers, and a "Download all" a visitor can actually click is
   * a give-away, not a demo. Every download path in the engine and
   * every affordance in all 23 designs reads this one flag.
   */
  downloadEnabled?: boolean;
}

/**
 * A complete public-gallery payload for a design, in memory. Shared by
 * the thumbs and the marketing page's live demos (MKTG-GAL14c).
 */
export function buildDemoPayload(p: DemoPayloadOptions): PublicGalleryPayload {
  const now = new Date().toISOString();
  const album = p.photos && p.photos.length > 0 ? p.photos : SAMPLE_ALBUM;
  const photos = album.map((ph, i) => ({
    id: ph.id,
    file_url: ph.url,
    thumbnail_url: ph.url,
    name: ph.name ?? `Photo ${i + 1}`,
    sort_order: i,
    width: ph.width ?? null,
    height: ph.height ?? null,
    ai_tags: null,
    ai_scene: null,
    ai_dominant_colors: null,
  }));
  const galleryId = p.galleryId ?? 'demo-thumb';
  const videos = isFilmDesign(p.template)
    ? SAMPLE_FILMS.map(([file_name, duration_seconds, posterIndex], i) => ({
        id: `${galleryId}-film-${i + 1}`,
        file_name,
        mux_playback_id: p.filmSource?.playbackId ?? 'demo-thumb',
        mux_status: 'ready',
        duration_seconds,
        mux_mp4_url: p.filmSource?.mp4Url ?? null,
        poster_url: album[posterIndex % album.length].url,
      }))
    : [];
  return {
    locked: false,
    gallery: {
      id: galleryId,
      name: p.name.trim() || 'Your gallery',
      description: null,
      is_public: true,
      download_enabled: p.downloadEnabled ?? true,
      proofing_enabled: true,
      proofing_locked: false,
      proofing_pick_limit: null,
      proofing_message: null,
      cover_photo_id: photos[0]?.id ?? null,
      created_at: now,
      updated_at: now,
      template: p.template,
      customization: p.customization ?? {},
      pin_required: false,
    },
    photos,
    branding: {
      studio_name: p.studioName?.trim() || 'Your studio',
      logo_url: p.logoUrl ?? null,
      brand_color: p.brandColor ?? null,
    },
    watermark: null,
    videos,
  } as unknown as PublicGalleryPayload;
}

function LiveDesign({ payload }: { payload: PublicGalleryPayload }) {
  const engine = useGalleryEngine(payload, { demoBadge: true });
  useGoogleFonts(googleFontsHref(engine.theme));
  const Template = getLazyTemplate(engine.templateId) ?? getLazyTemplate(DEFAULT_TEMPLATE_ID);
  if (!Template) return null;
  return (
    <Suspense fallback={null}>
      <Template engine={engine} />
    </Suspense>
  );
}

export function GalleryDesignThumb({
  template,
  name,
  studioName,
  logoUrl,
  brandColor,
  customization,
  photos,
  frameWidth = 1280,
  downloadEnabled = true,
  className,
}: GalleryDesignThumbProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setBox({ w: r.width, h: r.height });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    let io: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setInView(true);
            io?.disconnect();
          }
        },
        { rootMargin: '300px' },
      );
      io.observe(el);
    } else {
      setInView(true);
    }
    return () => {
      ro?.disconnect();
      io?.disconnect();
    };
  }, []);

  const photoKey = photos?.map((p) => p.id).join(',') ?? '';
  const payload = useMemo(
    () => buildDemoPayload({ template, name, studioName, logoUrl, brandColor, customization, photos, downloadEnabled }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [template, name, studioName, logoUrl, brandColor, customization, photoKey, downloadEnabled],
  );

  // The ground shown until the design mounts — the studio's chosen
  // background when there is one (GALLERY-2), so it doesn't flash the
  // design's own first.
  const ground = useMemo(
    () =>
      resolveTheme(GALLERY_THEMES[template] ?? GALLERY_THEMES[DEFAULT_TEMPLATE_ID], customization ?? {}, brandColor ?? null).bg,
    [template, customization, brandColor],
  );
  const scale = box.w > 0 ? box.w / frameWidth : 0;

  return (
    <div
      ref={ref}
      className={className}
      aria-hidden
      style={{ position: 'relative', overflow: 'hidden', background: ground, pointerEvents: 'none', userSelect: 'none' }}
    >
      {inView && scale > 0 && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: frameWidth,
            height: box.h / scale,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            overflow: 'hidden',
          }}
        >
          <LiveDesign payload={payload} />
        </div>
      )}
    </div>
  );
}

export default GalleryDesignThumb;
